// Details for one restroom: its numbers, its visits over time with each cleaning threshold marked,
// its queue time, and when each cleaning came due.
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/atoms/Icon';
import { StatTile } from '../../Concession/components/ConcessionUI';
import RestroomIcon from './RestroomIcon';
import VisitsChart from '../charts/VisitsChart';
import GateQueueChart from '../../Gates/charts/GateQueueChart';
import '../../Gates/gates.css';
import { CLEANING_THRESHOLD, cycleColor, minuteToClock, formatDueIn, formatRate, queueColor } from '../facilitiesMetrics';

const RestroomModal = ({ stats, isLive, currentGame, range = null, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!stats) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [stats, onClose]);

  if (!stats) return null;
  const { restroom, label, series, visits, dueCount, dueTimes, peak, cycle, rate, dueIn, queue, queueNow, queuePeak } = stats;
  const showQueue = queue.length > 0 || restroom.hasQueueSensors;

  return createPortal(
    <div className="fx-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="fx-modal" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={dialogRef}>
        <div className="fx-modal-head">
          <RestroomIcon imageURL={restroom.imageURL} className="fx-modal-icon" />
          <div className="fx-modal-titles">
            <h2 className="fx-modal-title">{label}</h2>
            {restroom.area && label.indexOf(restroom.area) === -1 && <div className="fx-modal-sub">{restroom.area}</div>}
          </div>
          <button type="button" className="fx-modal-close" onClick={onClose} aria-label="Close">
            <Icon name="x-lg" />
          </button>
        </div>

        <div className="fx-modal-body">
          <div className="cx-stat-grid fx-modal-stats">
            <StatTile
              label={isLive ? 'Entries today' : 'Entries'}
              value={visits.toLocaleString()}
              caption={isLive ? `${formatRate(rate)} / min now` : undefined}
            />
            {showQueue && isLive && (
              <StatTile
                label="Queue now"
                value={queueNow ? `${queueNow.value} min` : '—'}
                accent={queueNow ? queueColor(queueNow.value) : undefined}
                caption={queueNow ? `As of ${minuteToClock(queueNow.minute)}` : 'No recent reading'}
              />
            )}
            {showQueue && (
              <StatTile
                label="Peak queue"
                value={queuePeak ? `${queuePeak.value} min` : '—'}
                accent={queuePeak ? queueColor(queuePeak.value) : undefined}
                meta={queuePeak ? minuteToClock(queuePeak.minute) : undefined}
                caption={queuePeak ? undefined : 'No queue readings'}
              />
            )}
            {isLive && (
              <StatTile
                label="Next cleaning"
                value={`${cycle.since} / ${CLEANING_THRESHOLD}`}
                accent={cycleColor(cycle)}
                caption={dueIn != null ? `${cycle.toGo} to go · due in ${formatDueIn(dueIn)}` : `${cycle.toGo} entries to go`}
              />
            )}
            <StatTile
              label="Cleanings due"
              value={dueCount}
              caption={`One every ${CLEANING_THRESHOLD} entries`}
            />
            {!(isLive && showQueue) && (
              <StatTile
                label="Busiest 15 min"
                value={peak ? peak.total.toLocaleString() : '—'}
                meta={peak ? `${minuteToClock(peak.start)} – ${minuteToClock(peak.end)}` : undefined}
                caption={peak ? 'entries' : 'No entries recorded'}
              />
            )}
          </div>

          {queue.length > 0 && <GateQueueChart series={queue} currentGame={currentGame} range={range} afterStart={null} who="restroom" />}
          <VisitsChart series={series} currentGame={currentGame} range={range} who={label} height={260} showDue />

          <div className="fx-due-list">
            <div className="fx-due-head">
              <h3 className="cx-panel-title">When cleanings came due</h3>
              <span className="fx-due-note">Estimated from entries until cleaning records are available.</span>
            </div>
            {dueTimes.length ? (
              <ol>
                {dueTimes.map((d) => (
                  <li key={d.n}>
                    <span className="fx-due-n">Cleaning {d.n}</span>
                    <span className="fx-due-at">{minuteToClock(d.minute)}</span>
                    <span className="fx-due-visits">{(d.n * CLEANING_THRESHOLD).toLocaleString()} entries{range ? ' in window' : ''}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="fx-due-empty">
                No cleaning came due{range ? ' in this window' : ''}: fewer than {CLEANING_THRESHOLD} entries.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RestroomModal;
